import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";

function fmt(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

const TIER_COPY = {
  free: { label: "Free Account", detail: "One lifetime 6-question diagnostic. No reports, re-practice, restart, or study plan." },
  paid: { label: "Paid Account", detail: "Full 30-day access. Five interview sessions per day." },
  beta: { label: "Beta Tester Account", detail: "Full 30-day access. No daily interview-session limit. No payment." },
  admin: { label: "Administrator", detail: "Unrestricted administrative access." },
};

export function AdminSessionAllowancePanel({ userId, isAdminUser = false }) {
  const [data, setData] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [allowanceResult, accessResult] = await Promise.all([
        api.get(`/admin/session-allowance/${userId}`),
        api.get(`/admin/users/${userId}/access-entitlement`),
      ]);
      setData(allowanceResult.data);
      setAccess(accessResult.data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
      setData(null);
      setAccess(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const changeTier = async (tier) => {
    if (working || isAdminUser || tier === access?.access_tier) return;
    const label = TIER_COPY[tier]?.label || tier;
    const reason = window.prompt(`Change this user to ${label}?\n\nReason (required for audit):`, "Testing / account administration");
    if (!reason || !reason.trim()) return;
    if (!window.confirm(`Set this user to ${label}?${tier === "paid" || tier === "beta" ? "\n\nA new 30-day access period will start now." : ""}`)) return;

    setWorking(true);
    try {
      await api.patch(`/admin/users/${userId}/access-entitlement`, {
        access_tier: tier,
        days: 30,
        reason: reason.trim(),
      });
      toast.success(`Access changed to ${label}`);
      await load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setWorking(false);
    }
  };

  const resetFreeTrial = async () => {
    if (working || isAdminUser) return;
    const reason = window.prompt("Reset this user's one-time Free diagnostic?\n\nUse this only for a genuine technical failure. Reason is required for audit:", "Technical failure");
    if (!reason || !reason.trim()) return;
    if (!window.confirm("Restore this user's one-time Free diagnostic?")) return;
    setWorking(true);
    try {
      await api.post(`/admin/users/${userId}/free-trial/reset`, { reason: reason.trim() });
      toast.success("Free diagnostic restored");
      await load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setWorking(false);
    }
  };

  const toggleLimit = async () => {
    if (working || isAdminUser || data?.admin_exempt || access?.access_tier !== "paid") return;
    const disabled = !Boolean(data?.limit_disabled);
    const actionLabel = disabled ? "Disable daily session limit" : "Enable daily session limit";
    const reason = window.prompt(`${actionLabel}\n\nReason (required for audit):`, "");
    if (!reason || !reason.trim()) return;
    if (!window.confirm(`${actionLabel} for this Paid Account?`)) return;
    setWorking(true);
    try {
      await api.post(`/admin/session-allowance/${userId}/limit-exemption`, { disabled, reason: reason.trim() });
      toast.success(disabled ? "Daily session limit disabled" : "Daily session limit enabled");
      await load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setWorking(false);
    }
  };

  const adjust = async (action, amount = 1) => {
    if (!data?.current || working || access?.access_tier !== "paid") return;
    const labels = {
      restore_one: "Restore 1 interview session for today",
      grant_extra: `Grant ${amount} extra interview session${amount === 1 ? "" : "s"} for today`,
      set_remaining: `Set remaining interview sessions to ${amount} for today`,
      reset_today: "Reset today's interview session usage",
      clear_bonus: "Clear manual bonus sessions",
    };
    const reason = window.prompt(`${labels[action]}\n\nReason (required for audit):`, "");
    if (!reason || !reason.trim()) return;
    if (["set_remaining", "reset_today", "clear_bonus"].includes(action) && !window.confirm(`${labels[action]}?`)) return;
    setWorking(true);
    try {
      await api.post(`/admin/session-allowance/${userId}/adjust`, { action, amount, reason: reason.trim() });
      toast.success(labels[action]);
      await load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setWorking(false);
    }
  };

  const grantExtra = async () => {
    const raw = window.prompt("How many extra interview sessions should be available today?", "1");
    if (raw === null) return;
    const amount = parseInt(raw, 10);
    if (Number.isNaN(amount) || amount < 1 || amount > 20) return toast.error("Enter a whole number from 1 to 20");
    await adjust("grant_extra", amount);
  };

  const setRemaining = async () => {
    const raw = window.prompt("How many interview sessions should remain available today?", String(data?.current?.remaining ?? 0));
    if (raw === null) return;
    const amount = parseInt(raw, 10);
    if (Number.isNaN(amount) || amount < 0 || amount > 20) return toast.error("Enter a whole number from 0 to 20");
    await adjust("set_remaining", amount);
  };

  const tier = isAdminUser ? "admin" : (access?.access_tier || "free");
  const tierCopy = TIER_COPY[tier] || TIER_COPY.free;
  const limitDisabled = Boolean(data?.limit_disabled);
  const manualAdjustment = Number(data?.current?.manual_adjustment ?? data?.current?.manual_bonus ?? 0);

  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)]/40 p-4" data-testid="admin-session-allowance-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">Account Access & Interview Allowance</div>
          <p className="mt-1 text-xs text-[color:var(--text-secondary)]">Product access tier is separate from the account's active/suspended status.</p>
        </div>
        <button type="button" onClick={load} disabled={loading || working} className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-xs disabled:opacity-40">Refresh</button>
      </div>

      {loading && !access ? (
        <div className="mt-4 text-sm text-[color:var(--text-secondary)]">Loading access…</div>
      ) : (
        <>
          <div className="mt-4 rounded-xl border border-[color:var(--border-stone)] bg-white p-4" data-testid="admin-access-tier">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">Current access tier</div>
                <div className="mt-1 text-lg font-semibold text-[color:var(--text-primary)]">{tierCopy.label}</div>
                <p className="mt-1 max-w-xl text-xs text-[color:var(--text-secondary)]">{tierCopy.detail}</p>
                {(tier === "paid" || tier === "beta") && <p className="mt-2 text-xs"><span className="text-[color:var(--text-secondary)]">Expires:</span> {fmt(access?.access_expires_at)}</p>}
                {access?.expired && <p className="mt-2 text-xs font-medium text-[color:var(--score-bad)]">The previous full-access period expired; effective access has fallen back to Free.</p>}
                {access?.legacy_beta && <p className="mt-2 text-xs text-[color:var(--text-secondary)]">Legacy Beta approval is currently preserving Beta access until an explicit tier is assigned.</p>}
              </div>
              {!isAdminUser && (
                <div className="flex flex-wrap gap-2">
                  <TierButton active={tier === "free"} disabled={working} onClick={() => changeTier("free")}>Free</TierButton>
                  <TierButton active={tier === "paid"} disabled={working} onClick={() => changeTier("paid")}>Paid · 30 days</TierButton>
                  <TierButton active={tier === "beta"} disabled={working} onClick={() => changeTier("beta")}>Beta · 30 days</TierButton>
                </div>
              )}
            </div>

            {!isAdminUser && (
              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-[color:var(--bg-surface-hover)] p-3 text-xs">
                <span><strong>Free diagnostic:</strong> {access?.free_trial_used ? "Used" : "Available"}</span>
                {access?.free_trial_used_at && <span className="text-[color:var(--text-secondary)]">Used {fmt(access.free_trial_used_at)}</span>}
                {access?.free_trial_used && <Action onClick={resetFreeTrial} disabled={working}>Reset after technical failure</Action>}
              </div>
            )}
          </div>

          {tier === "admin" || tier === "beta" ? (
            <div className="mt-4 rounded-xl border border-[color:var(--brand)]/20 bg-white p-3 text-sm">{tier === "admin" ? "Admin account" : "Beta Tester Account"} — no daily interview-session limit.</div>
          ) : tier === "free" ? (
            <div className="mt-4 rounded-xl border border-[color:var(--border-stone)] bg-white p-3 text-sm">Free Account — the daily allowance does not apply. This account can use only its one-time 6-question diagnostic.</div>
          ) : (
            <PaidAllowance
              data={data}
              working={working}
              limitDisabled={limitDisabled}
              manualAdjustment={manualAdjustment}
              toggleLimit={toggleLimit}
              adjust={adjust}
              grantExtra={grantExtra}
              setRemaining={setRemaining}
            />
          )}

          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] mb-2">Recent interview activity</div>
            {data?.recent_interviews?.length ? (
              <div className="overflow-x-auto rounded-lg border border-[color:var(--border-stone)] bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-[color:var(--bg-surface-hover)] text-[color:var(--text-secondary)] uppercase tracking-widest"><tr><th className="text-left px-3 py-2">Created</th><th className="text-left px-3 py-2">Session</th><th className="text-left px-3 py-2">Format</th><th className="text-left px-3 py-2">Status</th></tr></thead>
                  <tbody>{data.recent_interviews.map((row) => <tr key={row.session_id} className="border-t border-[color:var(--border-stone)]"><td className="px-3 py-1.5 whitespace-nowrap">{fmt(row.created_at)}</td><td className="px-3 py-1.5 font-mono whitespace-nowrap">{row.session_id}</td><td className="px-3 py-1.5">{row.parent_session_id ? "Re-practice" : (row.practice_format || "—")}</td><td className="px-3 py-1.5">{row.status || "—"}</td></tr>)}</tbody>
                </table>
              </div>
            ) : <div className="text-xs text-[color:var(--text-secondary)]">No recent interview activity.</div>}
          </div>
        </>
      )}
    </section>
  );
}

function PaidAllowance({ data, working, limitDisabled, manualAdjustment, toggleLimit, adjust, grantExtra, setRemaining }) {
  const manualAdjustmentDisplay = `${manualAdjustment >= 0 ? "+" : ""}${manualAdjustment}`;
  return (
    <div className="mt-4 rounded-xl border border-[color:var(--border-stone)] bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><div className="text-xs font-semibold">Paid daily session limit: {limitDisabled ? "Off — Unlimited (manual exception)" : "On — 5/day"}</div><p className="mt-1 text-xs text-[color:var(--text-secondary)]">Paid Accounts normally receive five interview sessions per local calendar day.</p></div>
        <Action onClick={toggleLimit} disabled={working}>{limitDisabled ? "Restore 5/day limit" : "Disable limit exceptionally"}</Action>
      </div>
      {data?.current && (
        <>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
            <Metric label="Sessions used" value={data.current.used} />
            <Metric label="Base limit" value={data.current.base_limit} />
            <Metric label="Manual adjustment" value={manualAdjustmentDisplay} />
            <Metric label="Effective limit" value={limitDisabled ? "Unlimited" : data.current.limit} />
            <Metric label="Remaining" value={limitDisabled ? "Unlimited" : data.current.remaining} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Action onClick={() => adjust("restore_one")} disabled={working || limitDisabled || data.current.used <= 0}>Restore 1 session</Action>
            <Action onClick={grantExtra} disabled={working || limitDisabled}>Grant extra sessions</Action>
            <Action onClick={setRemaining} disabled={working || limitDisabled}>Set remaining sessions</Action>
            <Action onClick={() => adjust("reset_today")} disabled={working || limitDisabled}>Reset today's usage</Action>
            <Action onClick={() => adjust("clear_bonus")} disabled={working || limitDisabled || manualAdjustment === 0}>Clear manual adjustment</Action>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return <div className="rounded-xl border border-[color:var(--border-stone)] bg-white p-3"><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">{label}</div><div className="mt-1 text-lg font-semibold">{value}</div></div>;
}

function Action({ children, onClick, disabled }) {
  return <button type="button" onClick={onClick} disabled={disabled} className="rounded-full border border-[color:var(--border-stone)] bg-white px-4 py-1.5 text-xs font-medium hover:bg-[color:var(--bg-surface-hover)] disabled:opacity-40 disabled:cursor-not-allowed">{children}</button>;
}

function TierButton({ children, active, onClick, disabled }) {
  return <button type="button" onClick={onClick} disabled={disabled || active} className={`rounded-full px-4 py-2 text-xs font-medium border ${active ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-white" : "border-[color:var(--border-stone)] bg-white hover:bg-[color:var(--bg-surface-hover)]"} disabled:cursor-default`}>{children}</button>;
}
